import { Button, ButtonTooltip, Card, Col, HeaderRow, LoaderOverlay, Row } from "@emanprague/ppaskit";
import type { ViewComponent } from "@frui.ts/views";
import { registerView, View } from "@frui.ts/views";
import { observer } from "mobx-react-lite";
import React from "react";
import BannersPageViewModel from "viewModels/admin/bannersPageViewModel";
import type { DataColumn } from "controls/tableHelper";
import { createColumnsContext } from "controls/tableHelper";
import { Check, CollectionCheck, Input } from "@frui.ts/bootstrap";
import { ResponsiveTable } from "@frui.ts/dataviews";
import FilterButton from "controls/filterButton";
import DatePicker from "controls/twoway/DatePicker";
import iconEdit from "@emanprague/ppaskit/dist/assets/icons/Edit Light.svg";
import iconEye from "@emanprague/ppaskit/dist/assets/icons/Eye Light.svg";
import iconDelete from "@emanprague/ppaskit/dist/assets/icons/Delete Light.svg";
import EmptyDataMessage from "components/EmptyDataMessage";
import type BannerListItem from "entities/bannerListItem";
import { formatRange } from "helpers/utils";

const columns: DataColumn<BannerListItem, BannersPageViewModel>[] = [
  {
    titleFactory: ({ vm }) => <Check custom target={vm} property="isAllSelected" threeState label="" />,
    valueFormatter: ({ item, context }) => (
      <CollectionCheck custom label="" target={context.vm} property="selectedItems" value={item} />
    ),
    responsiveVisible: false,
  },
  {
    property: "id",
    titleFactory: ({ vm, ta }) => (
      <>
        {ta("banner", "id")}
        <FilterButton
          id="idFilter"
          isActive={!!vm.filter.id}
          clearLabel={vm.translateGeneral("cancel_button")}
          confirmLabel={vm.translateGeneral("confirm")}
          onClear={() => vm.clearFilter("id")}
          onConfirm={vm.applyFilterAndLoad}>
          <Input target={vm.filter} property="id" placeholder={vm.translateGeneral("search")} />
        </FilterButton>
      </>
    ),
    responsiveTitleFactory: ({ ta }) => ta("banner", "id"),
  },
  {
    property: "title",
    titleFactory: ({ vm, ta }) => (
      <>
        {ta("banner", "title")}
        <FilterButton
          id="idFilter"
          isActive={!!vm.filter.title}
          clearLabel={vm.translateGeneral("cancel_button")}
          confirmLabel={vm.translateGeneral("confirm")}
          onClear={() => vm.clearFilter("title")}
          onConfirm={vm.applyFilterAndLoad}>
          <Input target={vm.filter} property="title" placeholder={vm.translateGeneral("search")} />
        </FilterButton>
      </>
    ),
    valueFormatter: ({ value }) => <span className="text-nowrap">{value}</span>,
    responsiveTitleFactory: ({ ta }) => ta("banner", "title"),
  },
  {
    property: "type",
    titleFactory: ({ vm, ta }) => (
      <>
        {ta("banner", "type")}
        <FilterButton
          id="stateFilter"
          isActive={!!vm.filter.type}
          clearLabel={vm.translateGeneral("cancel_button")}
          confirmLabel={vm.translateGeneral("confirm")}
          onClear={() => vm.clearFilter("type")}
          onConfirm={vm.applyFilterAndLoad}>
          {vm.bannerTypes.map(item => (
            <Check
              custom
              key={item.id}
              value={item.id}
              target={vm.filter}
              property="type"
              label={item.name}
              className="mt-1"
              type="radio"
            />
          ))}
        </FilterButton>
      </>
    ),
    valueFormatter: ({ value, context }) => context.vm.localization.translateGeneral(`admin.banners.detail.${value}_type`),
    responsiveTitleFactory: ({ ta }) => ta("banner", "type"),
  },
  {
    titleFactory: ({ vm, ta }) => (
      <>
        {ta("banner", "validity")}
        <FilterButton
          id="validityFilter"
          isActive={!!vm.filter.validFrom || !!vm.filter.validTo}
          clearLabel={vm.translateGeneral("cancel_button")}
          confirmLabel={vm.translateGeneral("confirm")}
          onClear={() => vm.clearFilter("validFrom", "validTo")}
          onConfirm={vm.applyFilterAndLoad}>
          <Row className="align-items-center">
            <Col className="mr-n3">
              <span className="date-filter">{vm.translateGeneral("filter_date_from")}</span>
              <DatePicker
                target={vm.filter}
                property="validFrom"
                selectsStart
                startDate={vm.filter.validFrom}
                endDate={vm.filter.validTo}
                maxDate={vm.filter.validTo}
              />
            </Col>
            <Col>
              <span className="date-filter to">{vm.translateGeneral("filter_date_to")}</span>
              <DatePicker
                target={vm.filter}
                property="validTo"
                selectsEnd
                startDate={vm.filter.validFrom}
                endDate={vm.filter.validTo}
                minDate={vm.filter.validFrom}
              />
            </Col>
          </Row>
        </FilterButton>
      </>
    ),
    valueFormatter: ({ item, context }) =>
      formatRange(context.vm.localization.formatDate(item.validFrom), context.vm.localization.formatDate(item.validTo)),
    responsiveTitleFactory: ({ ta }) => ta("banner", "validity"),
  },
  {
    valueFormatter: ({ item, context }) => (
      <span className="text-nowrap">
        <ButtonTooltip tooltip={context.vm.translateGeneral("edit")} className="mr-2">
          <Button
            size="sm"
            variant="secondary"
            className="btn-responsive-block square-button"
            onClick={() => context.vm.openDetail(item.id)}>
            <img src={iconEdit} width={18} height={18} alt={context.vm.translateGeneral("edit")} />
          </Button>
        </ButtonTooltip>
        <ButtonTooltip tooltip={context.vm.translateGeneral("preview")} className="mr-2">
          <Button
            size="sm"
            variant="secondary"
            className="btn-responsive-block square-button"
            onClick={() => context.vm.openPreview(item.id)}>
            <img src={iconEye} width={18} height={18} alt={context.vm.translateGeneral("preview")} />
          </Button>
        </ButtonTooltip>
        <ButtonTooltip tooltip={context.vm.translateGeneral("delete")} last>
          <Button
            size="sm"
            variant="secondary"
            className="btn-responsive-block square-button"
            onClick={() => context.vm.openDelete([item.id])}>
            <img src={iconDelete} width={18} height={18} alt={context.vm.translateGeneral("delete")} />
          </Button>
        </ButtonTooltip>
      </span>
    ),
    responsiveTitle: false,
    cellClassName: "cell-right",
  },
];

const bannersPageView: ViewComponent<BannersPageViewModel> = observer(({ vm }) => {
  const { translateGeneral: tg } = vm.localization;

  return (
    <>
      <HeaderRow level={2} title={vm.translate("name")} className="mx-1 mx-sm-n3">
        <Button variant="primary" onClick={vm.addBanner}>
          {vm.translate("button_add_banner")}
        </Button>
      </HeaderRow>
      <Row className="mt-4">
        <Col xs={12}>
          <Card>
            <Card.Body>
              <LoaderOverlay loading={vm.busyWatcher.isBusy}>
                {vm.selectedItems.size > 0 && (
                  <Row className="mb-3 justify-content-between align-items-center">
                    <Col xs={12} className="d-flex justify-content-end">
                      <Button size="sm" onClick={() => vm.openDelete(vm.selectedBannersIds)}>
                        {vm.deleteButtonLabel}
                      </Button>
                    </Col>
                  </Row>
                )}

                <div className="responsive-table-wrapper">
                  <ResponsiveTable
                    className="table no-wrap"
                    items={vm.items}
                    itemKey="id"
                    columns={columns}
                    context={createColumnsContext(vm)}
                  />
                </div>
                {!vm.items?.length && <EmptyDataMessage translateGeneral={tg} />}

                {vm.canLoadData && (
                  <div className="load-more-button">
                    <Button onClick={vm.loadData} variant="secondary">
                      {tg("general.button_load_more")}
                    </Button>
                  </div>
                )}

                <View vm={vm.activeChild} />
              </LoaderOverlay>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </>
  );
});

registerView(bannersPageView, BannersPageViewModel);
